import { PanelLeft } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import { ProfileRailContext, useProfileRailCollapsed } from './profileRail';
import { ProfileSectionTitleContext } from './profileSectionTitle';
import type { ReactNode } from 'react';

/**
 * The sidebar profile layout — identity and navigation on the left, one panel of content on
 * the right.
 *
 * This is the first of the product's two canonical profile shapes. Use it when a record has
 * more about it than one screen holds, so the content splits into panels the reader picks
 * between: an account, a person, a case file. The other shape — header on top, content
 * below — suits a record whose content is a single continuous thing, typically one table.
 *
 * Both columns scroll independently. The page itself never scrolls: the shell is
 * height-capped and each side owns its own overflow, so the identity block stays put while
 * a long panel scrolls beside it.
 *
 * DELIBERATE DEVIATION from reference §7, recorded in CLAUDE.md. §7 describes a single
 * stacked layout (breadcrumb → identity banner → toolbar → status banner → content); the
 * product uses these two shapes instead.
 */
export function ProfileShell({
  header,
  sectionTitle,
  sidebar,
  banner,
  children,
  id,
  fill = false,
  railCollapsible = false,
}: {
  /**
   * A full-width identity strip above both columns — `<ProfileHeader>`.
   *
   * **Optional, and it MOVES the identity rather than repeating it.** A header carrying the
   * avatar, the name and the status beside a sidebar carrying the same three is one record
   * printed twice on one screen. A page that passes this drops `<ProfileIdentity>`.
   *
   * **The sidebar is then the panel picker, which `<ProfileFacts>` may LEAD.** That block is a
   * list of record facts, not a second identity — the 201 file's rail deliberately carries no
   * name row for that reason. It does repeat the unit and the assignment the strip above also
   * shows, by a product call on 27 Oct 2026 for a self-contained rail; it is the ONE page that
   * repeats anything, it is recorded in `.claude/rules/personnel.md`, and a second one is a
   * decision made out loud rather than a precedent to copy.
   */
  header?: ReactNode;
  /**
   * The open panel’s name, which every `<ProfileSection>` beneath this shell heads itself
   * with. Resolve it from the nav with `findNavLabel` rather than typing it: the rail and
   * the heading are one fact, and a second copy is one a rename leaves behind.
   */
  sectionTitle?: string;
  /** The left column: identity block (or, with a header, just the nav) and actions. */
  sidebar: ReactNode;
  /**
   * Record-level state, pinned above the panel content and shown on every panel — a lockout
   * or a suspension is true of the whole record, so it must not be something the reader
   * only discovers by opening the right tab.
   */
  banner?: ReactNode;
  /** The right column: the selected panel. */
  children: ReactNode;
  id?: string;
  /**
   * The open panel OWNS its region — the column stops scrolling and hands the panel its
   * height, and the readable-measure cap comes off so it takes the column's whole WIDTH. For a
   * panel whose folder tabs stand over tables that scroll inside them (Leaves): left to the
   * column, the tabs scrolled away with the rows, and at 896px the ledger scrolled sideways.
   * The cap exists for label/value cards; a table owns its own layout.
   */
  fill?: boolean;
  /**
   * The rail carries a MINIMIZE button, and the reader's choice is remembered.
   *
   * Opt-in, because a collapsed rail still has to work: everything the sidebar draws needs an
   * icon-width form or an honest reason to disappear. `<ProfileFacts>` hides — the facts it
   * carries are repeated on the header strip, which is the whole reason the 201 file was
   * allowed to repeat them — and `<ProfileNav>` draws its top level as glyphs. A page putting
   * `<ProfileIdentity>` in the rail has no such form for it yet, so it does not pass this.
   *
   * It REVERSES "the rail has no collapse at all, a fixed `w-72`" — a product call on
   * 27 Oct 2026 for the 201 file, whose panels are wide tables that want the room.
   */
  railCollapsible?: boolean;
}) {
  const { collapsed: railCollapsed, toggle: toggleRail, expand: expandRail } =
    useProfileRailCollapsed();

  const collapsed = railCollapsible && railCollapsed;

  /*
    ONE glyph in both states, as the app rail's own toggle settled on: a button whose icon
    changes under the cursor reads as two different controls, and the tooltip and the
    `aria-label` already say which way this one will go.
  */
  const toggleButton = (
    <Tooltip label={collapsed ? 'Expand sidebar' : 'Minimize sidebar'}>
      <button
        type="button"
        id={id ? `${id}-rail-toggle` : undefined}
        onClick={toggleRail}
        aria-expanded={!collapsed}
        aria-controls={id ? `${id}-rail` : undefined}
        aria-label={collapsed ? 'Expand sidebar' : 'Minimize sidebar'}
        className={[
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400',
          'transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none',
          'focus-visible:ring-2 focus-visible:ring-brand-300',
        ].join(' ')}
      >
        <PanelLeft className="h-4 w-4" aria-hidden="true" />
      </button>
    </Tooltip>
  );

  return (
    <ProfileSectionTitleContext.Provider value={sectionTitle}>
    <ProfileRailContext.Provider value={{ collapsed, expand: expandRail }}>
    <div id={id} className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      {header}

      {/* The two columns, under whatever the header took. `min-h-0` again, or the row
          grows past the shell instead of scrolling inside it. */}
      <div className="flex min-h-0 min-w-0 flex-1">
        {/*
          The rail animates its WIDTH rather than swapping between two layouts, so minimizing
          reads as one movement — the app rail's own call, kept here so the two behave alike.
          `w-16` is its collapsed width too: one measurement, twice.
        */}
        <aside
          id={id ? `${id}-rail` : undefined}
          aria-label="Record"
          data-collapsed={collapsed}
          className={[
            'modern-scrollbar flex shrink-0 flex-col overflow-y-auto border-r border-slate-200',
            'transition-[width] duration-200 ease-out motion-reduce:transition-none',
            collapsed ? 'w-16' : 'w-72',
          ].join(' ')}
        >
          {railCollapsible ? (
            /*
              The toggle's own row, above everything the page puts in the rail. Collapsed there
              is nothing to sit beside, so it centres; expanded it sits at the far end, opposite
              the content it folds away. It draws no rule of its own — the block under it owns
              the first seam, and two lines there would separate nothing.
            */
            <div
              className={`flex shrink-0 items-center px-2 pt-2 ${
                collapsed ? 'justify-center' : 'justify-end'
              }`}
            >
              {toggleButton}
            </div>
          ) : null}

          {sidebar}
        </aside>

        {/* min-w-0 is load-bearing: without it a wide panel pushes the sidebar off-screen
          instead of scrolling inside its own container. */}
        <main
          className={`modern-scrollbar min-h-0 min-w-0 flex-1 bg-slate-50/60 ${
            fill ? 'overflow-hidden' : 'overflow-y-auto'
          }`}
        >
          {/*
          Cards are capped to a readable measure and centred rather than stretched to the
          viewport. A label/value table run out to 1600px puts its value column a screen away
          from its label, and the eye loses the row on the way across.
        */}
          <div
            className={`mx-auto flex min-w-0 flex-col gap-5 p-5 ${
              fill ? 'h-full min-h-0' : 'max-w-4xl'
            }`}
          >
            {banner}
            {children}
          </div>
        </main>
      </div>
    </div>
    </ProfileRailContext.Provider>
    </ProfileSectionTitleContext.Provider>
  );
}
